import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { authClient } from '../lib/better-auth'

/**
 * Better Auth redirects the verified reset link here with a one-time
 * `?token=` value. The token is consumed by `resetPassword` and never stored.
 */
export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
})

type Phase = 'ready' | 'no-link' | 'done'

function ResetPasswordPage() {
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token'))
  const [phase, setPhase] = useState<Phase>(() => (token ? 'ready' : 'no-link'))
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (phase === 'no-link') {
    return (
      <Card className="w-full max-w-[420px] mx-auto my-8 sm:my-16" style={{ maxWidth: 420 }}>
        <h1 style={{ marginTop: 0 }}>Reset your password</h1>
        <p>
          This page sets a new password after you arrive from the reset email. No reset link
          detected — it may have expired.
        </p>
        <p style={{ color: '#6b7280', fontSize: 13 }}>
          Request a fresh link from the sign-in page ("Forgot password?").
        </p>
        <Link to="/login" style={{ color: '#1f2937' }}>
          Back to sign in
        </Link>
      </Card>
    )
  }

  if (phase === 'done') {
    return (
      <Card className="w-full max-w-[420px] mx-auto my-8 sm:my-16" style={{ maxWidth: 420 }}>
        <h1 style={{ marginTop: 0 }}>Password updated</h1>
        <p>Your old sessions were signed out for security. Sign in with your new password.</p>
        <Link to="/login" style={{ color: '#1f2937' }}>
          Back to sign in
        </Link>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-[420px] mx-auto my-8 sm:my-16" style={{ maxWidth: 420 }}>
      <h1 style={{ marginTop: 0 }}>Reset your password</h1>
      <p style={{ color: '#6b7280' }}>Choose a new password for your account.</p>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          setError(null)
          if (password.length < 8) {
            setError('Passwords need at least 8 characters.')
            return
          }
          if (password !== confirm) {
            setError('The two passwords do not match — please retype them.')
            return
          }
          setBusy(true)
          try {
            const { error: err } = await authClient.resetPassword({
              newPassword: password,
              token: token ?? undefined,
            })
            if (err) throw err
            setPhase('done')
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not update the password')
          } finally {
            setBusy(false)
          }
        }}
      >
        <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>New password</label>
        <Input
          type="password"
          required
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Min 8 characters"
          disabled={busy}
          className="w-full"
        />
        <label style={{ display: 'block', fontSize: 13, margin: '14px 0 6px' }}>
          Confirm new password
        </label>
        <Input
          type="password"
          required
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Repeat the password"
          disabled={busy}
          className="w-full"
        />
        {error ? <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>{error}</p> : null}
        <div style={{ marginTop: 16 }}>
          <Button type="submit" disabled={busy} data-testid="auth-reset-submit">
            {busy ? 'Updating...' : 'Set new password'}
          </Button>
        </div>
      </form>
    </Card>
  )
}
